import { catchError, distinctUntilChanged, map, retry, shareReplay, tap, switchMap, take } from 'rxjs/operators';
import { InjectionToken, Inject, Injectable, Optional, Component, Input, NgModule, Pipe, TemplateRef, ComponentFactoryResolver, ChangeDetectorRef, Directive, ElementRef, ViewContainerRef, defineInjectable, inject } from '@angular/core';
import { of, BehaviorSubject, combineLatest, from, Subject, Subscription } from 'rxjs';
import * as MessageFormat from 'messageformat';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class DefaultLoader {
    /**
     * @param {?} translations
     */
    constructor(translations) {
        this.translations = translations;
    }
    /**
     * @param {?} lang
     * @return {?}
     */
    getTranslation(lang) {
        return of(this.translations.get(lang) || {});
    }
}
/** @type {?} */
const TRANSLOCO_LOADER = new InjectionToken('TRANSLOCO_LOADER');

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * @param {?} obj
 * @param {?} path
 * @return {?}
 */
function getValue(obj, path) {
    /* For cases where the key is like: 'general.something.thing' */
    if (obj && obj.hasOwnProperty(path)) {
        return obj[path];
    }
    return path.split('.').reduce((/**
     * @param {?} p
     * @param {?} c
     * @return {?}
     */
    (p, c) => p && p[c]), obj);
}
/**
 * @param {?} obj
 * @param {?} prop
 * @param {?} val
 * @return {?}
 */
function setValue(obj, prop, val) {
    obj = Object.assign({}, obj);
    /** @type {?} */
    const split = prop.split('.');
    /** @type {?} */
    const lastIndex = split.length - 1;
    split.reduce((/**
     * @param {?} acc
     * @param {?} part
     * @param {?} index
     * @return {?}
     */
    (acc, part, index) => {
        if (index === lastIndex) {
            acc[part] = val;
        }
        else {
            acc[part] = Array.isArray(acc[part]) ? acc[part].slice() : Object.assign({}, acc[part]);
        }
        return acc && acc[part];
    }), obj);
    return obj;
}
/**
 * @param {?} collection
 * @return {?}
 */
function size(collection) {
    if (!collection) {
        return 0;
    }
    if (Array.isArray(collection)) {
        return collection.length;
    }
    if (isObject(collection)) {
        return Object.keys(collection).length;
    }
    return !!collection ? collection.length : 0;
}
/**
 * @param {?} collection
 * @return {?}
 */
function isEmpty(collection) {
    return size(collection) === 0;
}
/**
 * @param {?} val
 * @return {?}
 */
function isFunction(val) {
    return typeof val === 'function';
}
/**
 * @param {?} val
 * @return {?}
 */
function isString(val) {
    return typeof val === 'string';
}
/**
 * @param {?} val
 * @return {?}
 */
function isNumber(val) {
    return typeof val === 'number';
}
/**
 * @param {?} item
 * @return {?}
 */
function isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
}
/**
 * @param {?} val
 * @return {?}
 */
function coerceArray(val) {
    return Array.isArray(val) ? val : [val];
}
/**
 * @param {?} target
 * @param {...?} sources
 * @return {?}
 */
function mergeDeep(target, ...sources) {
    if (!sources.length)
        return target;
    /** @type {?} */
    const source = sources.shift();
    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key])
                    Object.assign(target, { [key]: {} });
                mergeDeep(target[key], source[key]);
            }
            else {
                Object.assign(target, { [key]: source[key] });
            }
        }
    }
    return mergeDeep(target, ...sources);
}
/*
 * @example
 *
 * given: lazy-page/en => lazy-page
 *
 */
/**
 * @param {?} lang
 * @return {?}
 */
function getScopeFromLang(lang) {
    /** @type {?} */
    const split = lang.split('/');
    split.pop();
    return split.join('/');
}
/*
 * @example
 *
 * given: lazy-page/en => en
 *
 */
/**
 * @param {?} lang
 * @return {?}
 */
function getLangFromScope(lang) {
    /** @type {?} */
    const split = lang.split('/');
    return split.pop();
}
/*
 * @example
 *
 * given: path-to-happiness => pathToHappiness
 * given: path_to_happiness => pathToHappiness
 * given: path-to_happiness => pathToHappiness
 *
 */
/**
 * @param {?} str
 * @return {?}
 */
function toCamelCase(str) {
    return str
        .replace(/(?:^\w|[A-Z]|\b\w)/g, (/**
     * @param {?} word
     * @param {?} index
     * @return {?}
     */
    (word, index) => (index == 0 ? word.toLowerCase() : word.toUpperCase())))
        .replace(/\s+|_|-|\//g, '');
}
/**
 * @return {?}
 */
function isBrowser() {
    return typeof window !== 'undefined';
}
/**
 * \@example
 *
 * getPipeValue('todos|scoped', 'scoped') [true, 'todos']
 * getPipeValue('en|static', 'static') [true, 'en']
 * getPipeValue('en', 'static') [false, 'en']
 * @param {?} str
 * @param {?} value
 * @param {?=} char
 * @return {?}
 */
function getPipeValue(str, value, char = '|') {
    if (isString(str)) {
        /** @type {?} */
        const splitted = str.split(char);
        /** @type {?} */
        const lastItem = splitted.pop();
        return lastItem === value ? [true, splitted.toString()] : [false, lastItem];
    }
    return [false, ''];
}
/**
 * @param {?} value
 * @return {?}
 */
function isNil(value) {
    return value === null || value === undefined;
}
/**
 * @param {?} value
 * @return {?}
 */
function isDefined(value) {
    return isNil(value) === false;
}
/**
 * @param {?} value
 * @return {?}
 */
function toNumber(value) {
    if (isNumber(value))
        return value;
    if (isString(value) && !isNaN(Number(value) - parseFloat(value))) {
        return Number(value);
    }
    return null;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const TRANSLOCO_TRANSPILER = new InjectionToken('TRANSLOCO_TRANSPILER');
class DefaultTranspiler {
    /**
     * @param {?} value
     * @param {?=} params
     * @param {?=} translation
     * @return {?}
     */
    transpile(value, params = {}, translation) {
        if (isString(value)) {
            return value.replace(/{{(.*?)}}/g, (/**
             * @param {?} _
             * @param {?} match
             * @return {?}
             */
            function (_, match) {
                match = match.trim();
                if (isDefined(params[match])) {
                    return params[match];
                }
                /** @type {?} */
                const fromTranslation = getValue(translation, match);
                return isDefined(fromTranslation) ? fromTranslation : '';
            }));
        }
        if (isObject(value) && params) {
            /**
             *
             * @example
             *
             * const en = {
             *  a: {
             *    b: {
             *      c: "Hello {{ value }}"
             *    }
             *  }
             * }
             *
             * const params =  {
             *  "b.c": { value: "Transloco "}
             * }
             *
             * service.selectTranslate('a', params);
             *
             * // the first param will be the result of `en.a`.
             * // the second param will be `params`.
             * parser.transpile(value, params, {});
             *
             *
             */
            Object.keys(params).forEach((/**
             * @param {?} p
             * @return {?}
             */
            p => {
                // get the value of "b.c" inside "a" => "Hello {{ value }}"
                /** @type {?} */
                const v = getValue(value, p);
                // get the params of "b.c" => { value: "Transloco" }
                /** @type {?} */
                const getParams = getValue(params, p);
                // transpile the value => "Hello Transloco"
                /** @type {?} */
                const transpiled = this.transpile(v, getParams, translation);
                // set "b.c" to `transpiled`
                value = setValue(value, p, transpiled);
            }));
        }
        return value;
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const TRANSLOCO_CONFIG = new InjectionToken('TRANSLOCO_CONFIG', {
    providedIn: 'root',
    factory: (/**
     * @return {?}
     */
    () => {
        return {};
    })
});
/** @type {?} */
const defaultConfig = {
    defaultLang: 'en',
    listenToLangChange: false,
    prodMode: false,
    failedRetries: 2,
    missingHandler: {
        allowEmpty: false
    }
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const TRANSLOCO_MISSING_HANDLER = new InjectionToken('TRANSLOCO_MISSING_HANDLER');
class DefaultHandler {
    /**
     * @param {?} key
     * @param {?} params
     * @param {?} config
     * @return {?}
     */
    handle(key, params, config) {
        if (!config.prodMode) {
            /** @type {?} */
            const msg = isFunction(key) ? `Missing value from translation callback` : `Missing translation for '${key}'`;
            console.warn(`%c ${msg} 🤔🕵🏻‍♀`, 'font-size: 12px; color: red');
        }
        return key;
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const TRANSLOCO_INTERCEPTOR = new InjectionToken('TRANSLOCO_INTERCEPTOR');
class DefaultInterceptor {
    /**
     * @param {?} translation
     * @param {?} lang
     * @return {?}
     */
    preSaveTranslation(translation, lang) {
        return translation;
    }
    /**
     * @param {?} key
     * @param {?} value
     * @param {?} lang
     * @return {?}
     */
    preSaveTranslationKey(key, value, lang) {
        return value;
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const TRANSLOCO_FALLBACK_STRATEGY = new InjectionToken('TRANSLOCO_FALLBACK_STRATEGY');
class DefaultFallbackStrategy {
    /**
     * @param {?} userConfig
     */
    constructor(userConfig) {
        this.userConfig = userConfig;
    }
    /**
     * @param {?} failedLang
     * @return {?}
     */
    getNextLangs(failedLang) {
        /** @type {?} */
        const fallbackLang = this.userConfig.fallbackLang;
        if (!fallbackLang) {
            throw new Error('When using the default fallback, a fallback language must be provided in the config!');
        }
        return Array.isArray(fallbackLang) ? fallbackLang : [fallbackLang];
    }
}
/** @nocollapse */
DefaultFallbackStrategy.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [TRANSLOCO_CONFIG,] }] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
let service;
/**
 * @template T
 * @param {?} key
 * @param {?=} params
 * @param {?=} lang
 * @return {?}
 */
function translate(key, params = {}, lang) {
    return service.translate(key, params, lang);
}
class TranslocoService {
    /**
     * @param {?} loader
     * @param {?} parser
     * @param {?} missingHandler
     * @param {?} interceptor
     * @param {?} userConfig
     * @param {?} fallbackStrategy
     */
    constructor(loader, parser, missingHandler, interceptor, userConfig, fallbackStrategy) {
        this.loader = loader;
        this.parser = parser;
        this.missingHandler = missingHandler;
        this.interceptor = interceptor;
        this.userConfig = userConfig;
        this.fallbackStrategy = fallbackStrategy;
        this.translations = new Map();
        this.cache = new Map();
        this.events = new Subject();
        this.events$ = this.events.asObservable();
        this.failedCounter = 0;
        this.failedLangs = new Set();
        if (!this.loader) {
            this.loader = new DefaultLoader(this.translations);
        }
        service = this;
        this.mergedConfig = Object.assign({}, defaultConfig, this.userConfig);
        this.setDefaultLang(this.mergedConfig.defaultLang);
        this.lang = new BehaviorSubject(this.getDefaultLang());
        this.langChanges$ = this.lang.asObservable().pipe(distinctUntilChanged());
        /**
         * When we have a failure, we want to define the next language that succeeded as the active
         */
        this.subscription = this.events$.subscribe((/**
         * @param {?} e
         * @return {?}
         */
        e => {
            if (e.type === 'translationLoadSuccess' && e.wasFailure) {
                // Handle scoped lang
                /** @type {?} */
                const lang = getLangFromScope(e.payload.lang);
                this.setActiveLang(lang);
            }
        }));
    }
    /**
     * @return {?}
     */
    get isSharedScope() {
        return this.config.scopeStrategy === 'shared';
    }
    /**
     * @return {?}
     */
    get config() {
        return this.mergedConfig;
    }
    /**
     * @return {?}
     */
    getDefaultLang() {
        return this.defaultLang;
    }
    /**
     * @param {?} lang
     * @return {?}
     */
    setDefaultLang(lang) {
        this.defaultLang = lang;
    }
    /**
     * @return {?}
     */
    getActiveLang() {
        return this.lang.getValue();
    }
    /**
     * @template THIS
     * @this {THIS}
     * @param {?} lang
     * @return {THIS}
     */
    setActiveLang(lang) {
        (/** @type {?} */ (this)).lang.next(lang);
        return (/** @type {?} */ (this));
    }
    /**
     * @param {?} lang
     * @param {?=} options
     * @return {?}
     */
    load(lang, options) {
        if (this.cache.has(lang) === false) {
            /** @type {?} */
            const mergedOptions = Object.assign({ fallbackLangs: null }, (options || {}));
            /** @type {?} */
            const load$ = from(this.loader.getTranslation(lang)).pipe(retry(this.mergedConfig.failedRetries), catchError((/**
             * @return {?}
             */
            () => this.handleFailure(lang, mergedOptions))), tap((/**
             * @param {?} translation
             * @return {?}
             */
            translation => this.handleSuccess(lang, translation))), shareReplay(1));
            this.cache.set(lang, load$);
        }
        return this.cache.get(lang);
    }
    /**
     * Gets the instant translated value of a key
     *
     * \@example
     *
     * translate<string>('hello')
     * translate('hello', { value: 'value' })
     * translate<string[]>(['hello', 'key'])
     * translate(t => t.a.b.c);
     * translate('hello', { }, 'en')
     * translate('hello-from-scope', { }, 'my-scope|scoped')
     * @template T
     * @param {?} key
     * @param {?=} params
     * @param {?=} lang
     * @return {?}
     */
    translate(key, params = {}, lang) {
        /** @type {?} */
        const withScope = this.completeScopeWithLang(lang);
        if (Array.isArray(key)) {
            return (/** @type {?} */ (key.map((/**
             * @param {?} k
             * @return {?}
             */
            k => this.translate(k, params, withScope)))));
        }
        if (!key) {
            return this.missingHandler.handle((/** @type {?} */ (key)), params, this.config);
        }
        /** @type {?} */
        const translation = this.translations.get(withScope || this.getActiveLang());
        if (!translation) {
            return (/** @type {?} */ (''));
        }
        /** @type {?} */
        const value = isFunction(key) ? key((/** @type {?} */ (translation)), params) : getValue(translation, key);
        if (!value) {
            if (this.mergedConfig.missingHandler.allowEmpty && value === '') {
                return (/** @type {?} */ (''));
            }
            return this.missingHandler.handle(key, params, this.config);
        }
        return (/** @type {?} */ (this.parser.transpile(value, params, translation)));
    }
    /**
     * Gets the translated value of a key as observable
     *
     * \@example
     *
     * selectTranslate<string>('hello').subscribe(value => ...)
     * selectTranslate<string>('hello', {}, 'es').subscribe(value => ...)
     * @template T
     * @param {?} key
     * @param {?=} params
     * @param {?=} lang
     * @return {?}
     */
    selectTranslate(key, params, lang) {
        /** @type {?} */
        const withScopeOrLang = this.completeScopeWithLang(lang);
        /** @type {?} */
        const load = (/**
         * @param {?} lang
         * @return {?}
         */
        lang => this.load(lang).pipe(map((/**
         * @return {?}
         */
        () => this.translate(key, params, lang)))));
        if (withScopeOrLang) {
            return load(withScopeOrLang);
        }
        else {
            // if the user doesn't pass lang, we need to listen to lang changes and update the value accordingly
            return this.langChanges$.pipe(switchMap((/**
             * @param {?} lang
             * @return {?}
             */
            lang => load(lang))));
        }
    }
    /**
     *  Translate a given value
     *
     * \@example
     *  transpile('Hello {{ value }}', { value: 'World' })
     *  transpile('Hello {{ value }}', { value: 'World' }, 'es')
     * @param {?} value
     * @param {?=} params
     * @param {?=} lang
     * @return {?}
     */
    transpile(value, params = {}, lang = this.getActiveLang()) {
        /** @type {?} */
        const translation = this.translations.get(lang);
        return this.parser.transpile(value, params, translation);
    }
    /**
     * @param {?=} lang
     * @return {?}
     */
    getTranslation(lang) {
        return lang ? this.translations.get(lang) || {} : this.translations;
    }
    /**
     * Gets an object of translations for a given language
     *
     * \@example
     *
     * selectTranslation().subscribe()
     * selectTranslation('es').subscribe()
     * @param {?=} lang
     * @return {?}
     */
    selectTranslation(lang) {
        /** @type {?} */
        const language = lang || this.getActiveLang();
        return this.load(language).pipe(map((/**
         * @return {?}
         */
        () => this.getTranslation(language))));
    }
    /**
     * Sets or merge a given translation object to current lang
     *
     * \@example
     *
     * setTranslation({ ... })
     * setTranslation({ ... }, 'en')
     * setTranslation({ ... }, 'admin-page/en', { merge: false } )
     * @param {?} data
     * @param {?=} lang
     * @param {?=} options
     * @return {?}
     */
    setTranslation(data, lang = this.getActiveLang(), options = {}) {
        /** @type {?} */
        const defaults = { merge: true, emitChange: true };
        /** @type {?} */
        const mergedOptions = Object.assign({}, defaults, options);
        /** @type {?} */
        const translation = this.getTranslation(lang) || {};
        /** @type {?} */
        const merged = mergedOptions.merge ? mergeDeep(translation, data) : data;
        this._setTranslation(lang, merged);
        mergedOptions.emitChange && this.setActiveLang(this.getActiveLang());
    }
    /**
     * Sets translation key with given value
     *
     * \@example
     *
     * setTranslationKey('key', 'value')
     * setTranslationKey('key.nested', 'value')
     * setTranslationKey('key.nested', 'value', 'en')
     * @param {?} key
     * @param {?} value
     * @param {?=} lang
     * @return {?}
     */
    setTranslationKey(key, value, lang = this.getActiveLang()) {
        /** @type {?} */
        const translation = this.getTranslation(lang);
        if (!isEmpty(translation)) {
            /** @type {?} */
            const withHook = this.interceptor.preSaveTranslationKey(key, value, lang);
            /** @type {?} */
            const newValue = setValue(translation, key, withHook);
            this.setTranslation(newValue, lang);
        }
    }
    /**
     * \@internal
     * When using the shared scope strategy you always want to make sure the global lang  is loaded
     * before loading the scope since you can access both via the pipe/directive.
     * @param {?} langName
     * @return {?}
     */
    _loadDependencies(langName) {
        /** @type {?} */
        const split = langName.split('/');
        const [lang] = split.slice(-1);
        if (split.length > 1 && this.isSharedScope && !size(this.getTranslation(lang))) {
            return combineLatest(this.load(lang), this.load(langName));
        }
        return this.load(langName);
    }
    /**
     * @private
     * @param {?} lang
     * @return {?}
     */
    completeScopeWithLang(lang) {
        const [isScoped, value] = getPipeValue(lang, 'scoped');
        if (isScoped) {
            lang = `${value}/${this.getActiveLang()}`;
        }
        return lang;
    }
    /**
     * @private
     * @param {?} lang
     * @param {?} translation
     * @return {?}
     */
    _setTranslation(lang, translation) {
        /** @type {?} */
        const withHook = this.interceptor.preSaveTranslation(translation, lang);
        this.translations.set(lang, withHook);
        const { scopeStrategy, scopeMapping = {} } = this.config;
        /** @type {?} */
        const currLang = getLangFromScope(lang);
        /** @type {?} */
        const scope = getScopeFromLang(lang);
        if (scope && scopeStrategy === 'shared') {
            /** @type {?} */
            const activeLang = this.getTranslation(currLang);
            /** @type {?} */
            const key = scopeMapping[scope] || toCamelCase(scope);
            /** @type {?} */
            const merged = setValue(activeLang, key, withHook);
            this.translations.set(currLang, merged);
        }
    }
    /**
     * @private
     * @param {?} lang
     * @param {?} translation
     * @return {?}
     */
    handleSuccess(lang, translation) {
        this.setTranslation(translation, lang, { emitChange: false });
        if (this.failedLangs.has(lang) === false) {
            if (!this.config.prodMode) {
                console.log(`%c 🍻 Translation Load Success: ${lang}`, 'background: #fff; color: hotpink;');
            }
            this.events.next({
                wasFailure: !!this.failedLangs.size,
                type: 'translationLoadSuccess',
                payload: {
                    lang
                }
            });
            this.failedCounter = 0;
        }
        else {
            this.cache.delete(lang);
            this.failedLangs.delete(lang);
        }
    }
    /**
     * @private
     * @param {?} lang
     * @param {?} mergedOptions
     * @return {?}
     */
    handleFailure(lang, mergedOptions) {
        /** @type {?} */
        const splitted = lang.split('/');
        this.failedLangs.add(lang);
        /** @type {?} */
        const fallbacks = mergedOptions.fallbackLangs || this.fallbackStrategy.getNextLangs(lang);
        /** @type {?} */
        const nextLang = fallbacks[this.failedCounter];
        /** @type {?} */
        const isFallbackLang = nextLang === splitted[splitted.length - 1];
        if (!nextLang || isFallbackLang) {
            /** @type {?} */
            let msg = `Unable to load translation and all the fallback languages`;
            if (splitted.length > 1) {
                msg += `, did you misspelled the scope name?`;
            }
            throw new Error(msg);
        }
        /** @type {?} */
        let resolveLang = nextLang;
        // if it's scoped lang
        if (splitted.length > 1) {
            // We need to resolve it to:
            // todos/langNotExists => todos/nextLang
            splitted[splitted.length - 1] = nextLang;
            resolveLang = splitted.join('/');
        }
        this.failedCounter++;
        this.events.next({
            type: 'translationLoadFailure',
            payload: {
                lang
            }
        });
        return this.load(resolveLang);
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
TranslocoService.decorators = [
    { type: Injectable, args: [{ providedIn: 'root' },] }
];
/** @nocollapse */
TranslocoService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [TRANSLOCO_LOADER,] }] },
    { type: undefined, decorators: [{ type: Inject, args: [TRANSLOCO_TRANSPILER,] }] },
    { type: undefined, decorators: [{ type: Inject, args: [TRANSLOCO_MISSING_HANDLER,] }] },
    { type: undefined, decorators: [{ type: Inject, args: [TRANSLOCO_INTERCEPTOR,] }] },
    { type: undefined, decorators: [{ type: Inject, args: [TRANSLOCO_CONFIG,] }] },
    { type: undefined, decorators: [{ type: Inject, args: [TRANSLOCO_FALLBACK_STRATEGY,] }] }
];
/** @nocollapse */ TranslocoService.ngInjectableDef = defineInjectable({ factory: function TranslocoService_Factory() { return new TranslocoService(inject(TRANSLOCO_LOADER, 8), inject(TRANSLOCO_TRANSPILER), inject(TRANSLOCO_MISSING_HANDLER), inject(TRANSLOCO_INTERCEPTOR), inject(TRANSLOCO_CONFIG), inject(TRANSLOCO_FALLBACK_STRATEGY)); }, token: TranslocoService, providedIn: "root" });

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class TranslocoLoaderComponent {
}
TranslocoLoaderComponent.decorators = [
    { type: Component, args: [{
                template: `
    <div class="transloco-loader-template" [innerHTML]="html"></div>
  `
            }] }
];
TranslocoLoaderComponent.propDecorators = {
    html: [{ type: Input }]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class TemplateHandler {
    /**
     * @param {?} view
     * @param {?} vcr
     */
    constructor(view, vcr) {
        this.view = view;
        this.vcr = vcr;
        this.injector = this.vcr.injector;
    }
    /**
     * @return {?}
     */
    attachView() {
        if (this.view instanceof TemplateRef) {
            this.vcr.createEmbeddedView(this.view);
        }
        else if (isString(this.view)) {
            /** @type {?} */
            const componentRef = this.createComponent(TranslocoLoaderComponent);
            componentRef.instance.html = this.view;
            componentRef.hostView.detectChanges();
        }
        else {
            this.createComponent(this.view);
        }
    }
    /**
     * @return {?}
     */
    detachView() {
        this.vcr.clear();
    }
    /**
     * @private
     * @template T
     * @param {?} cmp
     * @return {?}
     */
    createComponent(cmp) {
        /** @type {?} */
        const cfr = this.injector.get(ComponentFactoryResolver);
        /** @type {?} */
        const factory = cfr.resolveComponentFactory(cmp);
        return this.vcr.createComponent(factory);
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const TRANSLOCO_LANG = new InjectionToken('TRANSLOCO_LANG');

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const TRANSLOCO_LOADING_TEMPLATE = new InjectionToken('TRANSLOCO_LOADING_TEMPLATE');

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const TRANSLOCO_SCOPE = new InjectionToken('TRANSLOCO_SCOPE');

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * @param {?} service
 * @param {?} lang
 * @return {?}
 */
function shouldListenToLangChanges(service, lang) {
    const [hasStatic] = getPipeValue(lang, 'static');
    if (hasStatic === false) {
        // If we didn't get 'lang|static' check if it's set in the global level
        return service.config.listenToLangChange;
    }
    // We have 'lang|static' so don't listen to lang changes
    return false;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class TranslocoDirective {
    /**
     * @param {?} translocoService
     * @param {?} tpl
     * @param {?} providerScope
     * @param {?} providerLang
     * @param {?} providedLoadingTpl
     * @param {?} vcr
     * @param {?} cdr
     * @param {?} host
     */
    constructor(translocoService, tpl, providerScope, providerLang, providedLoadingTpl, vcr, cdr, host) {
        this.translocoService = translocoService;
        this.tpl = tpl;
        this.providerScope = providerScope;
        this.providerLang = providerLang;
        this.providedLoadingTpl = providedLoadingTpl;
        this.vcr = vcr;
        this.cdr = cdr;
        this.host = host;
        this.subscription = Subscription.EMPTY;
        this.params = {};
        this.loaderTplHandler = null;
        // Whether we already rendered the view once
        this.initialized = false;
    }
    /**
     * @return {?}
     */
    ngOnInit() {
        /** @type {?} */
        const loadingTpl = this.getLoadingTpl();
        if (loadingTpl) {
            this.loaderTplHandler = new TemplateHandler(loadingTpl, this.vcr);
            this.loaderTplHandler.attachView();
        }
        /** @type {?} */
        const listenToLangChange = shouldListenToLangChanges(this.translocoService, this.providerLang || this.inlineLang);
        this.subscription = this.translocoService.langChanges$
            .pipe(switchMap((/**
         * @return {?}
         */
        () => {
            /** @type {?} */
            const lang = this.getLang();
            /** @type {?} */
            const scope = this.getScope();
            this.langName = scope ? `${scope}/${lang}` : lang;
            return this.translocoService._loadDependencies(this.langName);
        })), listenToLangChange ? (/**
         * @param {?} source
         * @return {?}
         */
        source => source) : take(1))
            .subscribe((/**
         * @return {?}
         */
        () => {
            /* In case the scope strategy is set to 'shared' we want to load the scope's language instead of the scope
                    itself in order to expose the global translations as well.
                    the scopes translations are merged to the global when using this strategy */
            /** @type {?} */
            let targetLang = this.langName;
            /** @type {?} */
            const scope = this.getScope();
            if (scope) {
                targetLang = this.translocoService.isSharedScope ? this.getLang() : this.langName;
            }
            /** @type {?} */
            const translation = this.translocoService.getTranslation(targetLang);
            this.langName = targetLang;
            this.tpl === null ? this.simpleStrategy() : this.structuralStrategy(translation);
            this.cdr.markForCheck();
            this.initialized = true;
        }));
    }
    /**
     * @param {?} changes
     * @return {?}
     */
    ngOnChanges(changes) {
        // We need to support dynamic keys/params, so if this is not the first change CD cycle
        // we need to run the function again in order to update the value
        /** @type {?} */
        const notInit = Object.keys(changes).some((/**
         * @param {?} v
         * @return {?}
         */
        v => changes[v].firstChange === false));
        notInit && this.simpleStrategy();
    }
    /**
     * @private
     * @return {?}
     */
    simpleStrategy() {
        this.detachLoader();
        this.host.nativeElement.innerText = this.translocoService.translate(this.key, this.params, this.langName);
    }
    /**
     * @private
     * @param {?} data
     * @return {?}
     */
    structuralStrategy(data) {
        /** @type {?} */
        const translations = this.inlineRead ? getValue(data, this.inlineRead) : data;
        if (this.view) {
            this.view.context['$implicit'] = translations;
        }
        else {
            this.detachLoader();
            this.view = this.vcr.createEmbeddedView(this.tpl, {
                $implicit: translations
            });
        }
    }
    /**
     * @private
     * @return {?}
     */
    getLoadingTpl() {
        return this.inlineTpl || this.providedLoadingTpl;
    }
    // inline => providers
    /**
     * @private
     * @return {?}
     */
    getScope() {
        return this.inlineScope || this.providerScope;
    }
    // inline => providers => global
    /**
     * @private
     * @return {?}
     */
    getLang() {
        /**
         * When the user changes the lang we need to update
         * the view. Otherwise, the lang will remain the inline/provided lang
         */
        if (this.initialized) {
            return this.translocoService.getActiveLang();
        }
        if (this.inlineLang) {
            const [_, lang] = getPipeValue(this.inlineLang, 'static');
            return lang;
        }
        if (this.providerLang) {
            const [_, lang] = getPipeValue(this.providerLang, 'static');
            return lang;
        }
        return this.translocoService.getActiveLang();
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    /**
     * @private
     * @return {?}
     */
    detachLoader() {
        this.loaderTplHandler && this.loaderTplHandler.detachView();
    }
}
TranslocoDirective.decorators = [
    { type: Directive, args: [{
                selector: '[transloco]'
            },] }
];
/** @nocollapse */
TranslocoDirective.ctorParameters = () => [
    { type: TranslocoService },
    { type: TemplateRef, decorators: [{ type: Optional }] },
    { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [TRANSLOCO_SCOPE,] }] },
    { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [TRANSLOCO_LANG,] }] },
    { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [TRANSLOCO_LOADING_TEMPLATE,] }] },
    { type: ViewContainerRef },
    { type: ChangeDetectorRef },
    { type: ElementRef }
];
TranslocoDirective.propDecorators = {
    key: [{ type: Input, args: ['transloco',] }],
    params: [{ type: Input, args: ['translocoParams',] }],
    inlineScope: [{ type: Input, args: ['translocoScope',] }],
    inlineRead: [{ type: Input, args: ['translocoRead',] }],
    inlineLang: [{ type: Input, args: ['translocoLang',] }],
    inlineTpl: [{ type: Input, args: ['translocoLoadingTpl',] }]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class TranslocoPipe {
    /**
     * @param {?} translocoService
     * @param {?} providerScope
     * @param {?} providerLang
     * @param {?} cdr
     */
    constructor(translocoService, providerScope, providerLang, cdr) {
        this.translocoService = translocoService;
        this.providerScope = providerScope;
        this.providerLang = providerLang;
        this.cdr = cdr;
        this.subscription = Subscription.EMPTY;
        this.value = '';
        this.listenToLangChange = shouldListenToLangChanges(this.translocoService, this.providerLang);
    }
    /**
     * @param {?} key
     * @param {?=} params
     * @return {?}
     */
    transform(key, params = {}) {
        if (!key) {
            return key;
        }
        if (key === this.lastKey && JSON.stringify(params) === JSON.stringify(this.lastParams)) {
            return this.value;
        }
        /* Cache this key and params */
        this.lastKey = key;
        this.lastParams = params;
        /* Clean previous subscription if exists */
        this.subscription.unsubscribe();
        this.subscription = this.translocoService.langChanges$
            .pipe(switchMap((/**
         * @param {?} activeLang
         * @return {?}
         */
        activeLang => {
            /** @type {?} */
            const lang = this.providerLang || activeLang;
            this.langName = this.providerScope ? `${this.providerScope}/${lang}` : lang;
            return this.translocoService._loadDependencies(this.langName);
        })), this.listenToLangChange ? (/**
         * @param {?} source
         * @return {?}
         */
        source => source) : take(1))
            .subscribe((/**
         * @return {?}
         */
        () => this.updateValue(key, params)));
        return this.value;
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    /**
     * @private
     * @param {?} key
     * @param {?=} params
     * @return {?}
     */
    updateValue(key, params) {
        /** @type {?} */
        let targetLang = this.langName;
        /* In case the scope strategy is set to 'shared' we want to load the scope's language instead of the scope
            itself in order to expose the global translations as well.
            the scopes translations are merged to the global when using this strategy */
        /** @type {?} */
        const scope = getScopeFromLang(this.langName);
        if (scope) {
            targetLang = this.translocoService.isSharedScope ? getLangFromScope(this.langName) : this.langName;
        }
        /** @type {?} */
        const translation = (/** @type {?} */ (this.translocoService.translate(key, params, targetLang)));
        this.value = translation || key;
        this.cdr.markForCheck();
    }
}
TranslocoPipe.decorators = [
    { type: Pipe, args: [{
                name: 'transloco',
                pure: false
            },] }
];
/** @nocollapse */
TranslocoPipe.ctorParameters = () => [
    { type: TranslocoService },
    { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [TRANSLOCO_SCOPE,] }] },
    { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [TRANSLOCO_LANG,] }] },
    { type: ChangeDetectorRef }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class TranslocoParamsPipe {
    /**
     * @param {?} service
     * @param {?} missingHandler
     */
    constructor(service, missingHandler) {
        this.service = service;
        this.missingHandler = missingHandler;
    }
    /**
     * @param {?} value
     * @param {?=} params
     * @return {?}
     */
    transform(value, params) {
        if (!value) {
            this.missingHandler.handle(value, params, this.service.config);
        }
        return this.service.transpile(value, params);
    }
}
TranslocoParamsPipe.decorators = [
    { type: Pipe, args: [{
                name: 'translocoParams'
            },] }
];
/** @nocollapse */
TranslocoParamsPipe.ctorParameters = () => [
    { type: TranslocoService },
    { type: undefined, decorators: [{ type: Inject, args: [TRANSLOCO_MISSING_HANDLER,] }] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const defaultProviders = [
    {
        provide: TRANSLOCO_TRANSPILER,
        useClass: DefaultTranspiler
    },
    {
        provide: TRANSLOCO_MISSING_HANDLER,
        useClass: DefaultHandler
    },
    {
        provide: TRANSLOCO_INTERCEPTOR,
        useClass: DefaultInterceptor
    },
    {
        provide: TRANSLOCO_FALLBACK_STRATEGY,
        useClass: DefaultFallbackStrategy,
        deps: [TRANSLOCO_CONFIG]
    }
];
class TranslocoModule {
}
TranslocoModule.decorators = [
    { type: NgModule, args: [{
                declarations: [TranslocoDirective, TranslocoParamsPipe, TranslocoPipe, TranslocoLoaderComponent],
                providers: [defaultProviders],
                exports: [TranslocoDirective, TranslocoParamsPipe, TranslocoPipe],
                entryComponents: [TranslocoLoaderComponent]
            },] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class TestingLoader {
    /**
     * @param {?} langs
     */
    constructor(langs) {
        this.langs = langs;
    }
    /**
     * @param {?} lang
     * @return {?}
     */
    getTranslation(lang) {
        return of(this.langs[lang]);
    }
}
/** @nocollapse */
TestingLoader.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: ['translocoLangs',] }] }
];
class TranslocoTestingModule {
    /**
     * @param {?} langs
     * @param {?=} config
     * @return {?}
     */
    static withLangs(langs, config = {}) {
        return {
            ngModule: TranslocoTestingModule,
            providers: [
                {
                    provide: 'translocoLangs',
                    useValue: langs
                },
                {
                    provide: TRANSLOCO_LOADER,
                    useClass: TestingLoader
                },
                defaultProviders,
                {
                    provide: TRANSLOCO_CONFIG,
                    useValue: Object.assign({ prodMode: true }, config)
                }
            ]
        };
    }
}
TranslocoTestingModule.decorators = [
    { type: NgModule, args: [{
                exports: [TranslocoModule]
            },] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 *
 * @deprecated use \@ngneat/transloco-messageformat
 *
 */
class MessageFormatTranspiler {
    constructor() {
        this.defaultTranspiler = new DefaultTranspiler();
        //@ts-ignore
        this.messageFormat = new MessageFormat();
    }
    /**
     * @param {?} value
     * @param {?=} params
     * @param {?=} translation
     * @return {?}
     */
    transpile(value, params = {}, translation) {
        if (!value) {
            return value;
        }
        if (isObject(value) && params) {
            Object.keys(params).forEach((/**
             * @param {?} p
             * @return {?}
             */
            p => {
                /** @type {?} */
                const v = getValue((/** @type {?} */ (value)), p);
                /** @type {?} */
                const getParams = getValue(params, p);
                /** @type {?} */
                const transpiled = this.defaultTranspiler.transpile(v, getParams, translation);
                /** @type {?} */
                const message = this.messageFormat.compile(transpiled);
                value = setValue(value, p, message(params[p]));
            }));
        }
        else {
            /** @type {?} */
            const transpiled = this.defaultTranspiler.transpile(value, params, translation);
            /** @type {?} */
            const message = this.messageFormat.compile(transpiled);
            return message(params);
        }
        return value;
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Returns the language code name from the browser, e.g. "en"
 * @return {?}
 */
function getBrowserLang() {
    if (isBrowser() === false) {
        return undefined;
    }
    /** @type {?} */
    let browserLang = getBrowserCultureLang();
    if (browserLang.indexOf('-') !== -1) {
        browserLang = browserLang.split('-')[0];
    }
    if (browserLang.indexOf('_') !== -1) {
        browserLang = browserLang.split('_')[0];
    }
    return browserLang;
}
/**
 * Returns the culture language code name from the browser, e.g. "en-US"
 * @return {?}
 */
function getBrowserCultureLang() {
    if (isBrowser() === false) {
        return undefined;
    }
    /** @type {?} */
    const navigator = (/** @type {?} */ (window.navigator));
    /** @type {?} */
    let browserCultureLang = navigator.languages ? navigator.languages[0] : null;
    browserCultureLang = browserCultureLang || navigator.language || navigator.browserLanguage || navigator.userLanguage;
    return browserCultureLang;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

export { translate, TranslocoService, TranslocoDirective, TranslocoPipe, TranslocoModule, defaultProviders, TRANSLOCO_LOADER, TRANSLOCO_CONFIG, defaultConfig, TRANSLOCO_TRANSPILER, DefaultTranspiler, TranslocoParamsPipe, TRANSLOCO_SCOPE, TRANSLOCO_LOADING_TEMPLATE, TRANSLOCO_LANG, TestingLoader, TranslocoTestingModule, TemplateHandler, TRANSLOCO_INTERCEPTOR, TRANSLOCO_FALLBACK_STRATEGY, DefaultFallbackStrategy, MessageFormatTranspiler, TRANSLOCO_MISSING_HANDLER, getBrowserCultureLang, getBrowserLang, getValue, setValue, size, isEmpty, isFunction, isString, isNumber, isObject, coerceArray, mergeDeep, getScopeFromLang, getLangFromScope, toCamelCase, isBrowser, getPipeValue, isNil, isDefined, toNumber, TranslocoLoaderComponent as ɵc, DefaultHandler as ɵb, DefaultInterceptor as ɵa };

//# sourceMappingURL=ngneat-transloco.js.map